import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "./api";

const AuthCtx = createContext(null);

const CACHE_KEY = "confirmph_auth_cache_v1";

function readCache() {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj && obj.user) return obj;
  } catch (_e) { /* ignore */ }
  return null;
}

function writeCache(user, company) {
  if (typeof localStorage === "undefined") return;
  try {
    if (!user) {
      localStorage.removeItem(CACHE_KEY);
    } else {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ user, company }));
    }
  } catch (_e) { /* quota / private mode */ }
}

export function AuthProvider({ children }) {
  // Hydrate from localStorage cache so the UI can render instantly on
  // PWA launch: `/auth/me` still runs in the background to refresh.
  const initial = readCache();
  const [user, setUser] = useState(initial?.user || null);
  const [company, setCompany] = useState(initial?.company || null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("confirmph_token");
    if (!token) {
      setUser(null);
      setCompany(null);
      writeCache(null, null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setCompany(data.company);
      writeCache(data.user, data.company);
    } catch (e) {
      // Only nuke cached session if the server actively rejected us
      // (401/403). Network errors must NOT log the user out: the
      // PWA may have just launched offline.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("confirmph_token");
        writeCache(null, null);
        setUser(null);
        setCompany(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const _setFromAuthResponse = (data) => {
    localStorage.setItem("confirmph_token", data.access_token);
    setUser(data.user);
    setCompany(data.company);
    setSession(data.session || null);
    writeCache(data.user, data.company);
  };

  // login(identifier, password, { remember_device } = {})
  const login = async (identifier, password, opts = {}) => {
    const { data } = await api.post("/auth/login", {
      identifier,
      password,
      remember_device: !!opts.remember_device,
      device_label: opts.device_label,
    });
    _setFromAuthResponse(data);
    return data;
  };

  const registerCustomer = async (payload) => {
    const { data } = await api.post("/auth/register-customer", payload);
    _setFromAuthResponse(data);
    setCompany(null);
    writeCache(data.user, null);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    _setFromAuthResponse(data);
    return data;
  };

  const logout = async () => {
    // Best-effort backend revoke, never block the user from signing out
    // because of a network blip.
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("confirmph_token");
    writeCache(null, null);
    setUser(null);
    setCompany(null);
    setSession(null);
  };

  return (
    <AuthCtx.Provider value={{
      user, company, session, loading,
      login, register, registerCustomer, logout,
      refresh: fetchMe,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export { formatApiError };
